import { useId, useMemo, type HTMLAttributes, type ReactNode } from 'react';
import { InfoCircle } from 'iconsax-reactjs';
import { cx } from '../internal/cx';
import { FieldContext, type FieldContextValue, type FieldControlProps } from './fieldContext';
import styles from './Form.module.css';

export interface FieldProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  label: ReactNode;
  /** Help text shown under the label. */
  hint?: ReactNode;
  /** Validation message. When present, the control gets aria-invalid. */
  error?: ReactNode;
  required?: boolean;
  /** Show "(optional)" next to the label instead of marking required fields. */
  optional?: boolean;
  disabled?: boolean;
  /** Control id. Generated when omitted. */
  id?: string;
  /** Visually hide the label (it remains the accessible name). */
  hideLabel?: boolean;
  /**
   * The control. Either a component from this library (wired automatically through context)
   * or a render function receiving the props to spread on a custom control.
   */
  children: ReactNode | ((control: FieldControlProps) => ReactNode);
}

export function Field({
  label,
  hint,
  error,
  required = false,
  optional = false,
  disabled = false,
  id: idProp,
  hideLabel = false,
  className,
  children,
  ...rest
}: FieldProps) {
  const generatedId = useId();
  const id = idProp ?? `field-${generatedId}`;
  const labelId = `${id}-label`;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const invalid = Boolean(error);
  const describedBy = cx(hintId, errorId) || undefined;

  const context = useMemo<FieldContextValue>(
    () => ({ id, labelId, describedBy, invalid, required, disabled }),
    [id, labelId, describedBy, invalid, required, disabled],
  );

  const controlProps: FieldControlProps = {
    id,
    'aria-describedby': describedBy,
    'aria-invalid': invalid || undefined,
    required: required || undefined,
    disabled: disabled || undefined,
  };

  return (
    <div className={cx(styles.field, disabled && styles.fieldDisabled, className)} {...rest}>
      <label id={labelId} htmlFor={id} className={cx(styles.label, hideLabel && 'sr-only')}>
        {label}
        {required && (
          <span className={styles.required} aria-hidden="true">
            *
          </span>
        )}
        {optional && !required && <span className={styles.optional}>(optional)</span>}
      </label>
      <FieldContext.Provider value={context}>
        {typeof children === 'function' ? children(controlProps) : children}
      </FieldContext.Provider>
      {/* Hint below the control so controls in the same form row line up whether or not their
          fields have a hint. It stays linked through aria-describedby. */}
      {hint && (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className={styles.error}>
          <InfoCircle size={14} color="currentColor" variant="Bold" aria-hidden="true" />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
}
